import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';

import { NotifyItem, NotifyKind, NotifyService } from '../../services/notification/notify.service';

/** Cada cuánto se descuenta el tiempo restante. 50ms alcanza para una barra fluida. */
const TICK_MS = 50;

/** §4: 200ms de salida. El desmontaje espera esto para que se vea la animación. */
const SALIDA_MS = 200;

/**
 * Contenedor único de notificaciones. Se monta una sola vez en el shell de la
 * aplicación (`app.component.html`) y escucha al `NotifyService`.
 *
 * Vive acá el descuento del tiempo y la pausa por hover, porque son cosas de
 * presentación: el servicio sólo sabe qué notificaciones hay.
 *
 * El intervalo corre FUERA de la zona de Angular. Con 20 ticks por segundo,
 * dejarlo adentro dispararía change detection global todo el tiempo aunque no
 * haya nada que mostrar; los signals ya notifican el cambio por su cuenta.
 */
@Component({
  selector: 'app-notify-host',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notify-host.component.html',
  styleUrl: './notify-host.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotifyHostComponent implements OnInit, OnDestroy {
  private readonly svc = inject(NotifyService);
  private readonly zone = inject(NgZone);

  readonly items = this.svc.items;

  /**
   * §4: con `prefers-reduced-motion` no hay animaciones ni barra de progreso.
   * Se lee una sola vez al montar: el host vive lo que vive la aplicación y el
   * usuario no cambia esta preferencia a mitad de sesión.
   */
  readonly reducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  private timer: any = null;
  private readonly salidas = new Set<number>();

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.timer = setInterval(() => this.onTick(), TICK_MS);
    });
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.salidas.clear();
  }

  trackById(_index: number, item: NotifyItem): number {
    return item.id;
  }

  /** §5: los errores interrumpen al lector de pantalla; el resto no. */
  roleFor(item: NotifyItem): 'alert' | 'status' {
    return item.tipo === 'error' ? 'alert' : 'status';
  }

  /** Ancho de la barra de progreso, de 100% a 0%. */
  progresoDe(item: NotifyItem): number {
    if (item.duracion <= 0) return 0;
    return Math.max(0, Math.min(100, (item.restante / item.duracion) * 100));
  }

  /** La barra existe sólo si la notificación se cierra sola y hay animación. */
  muestraProgreso(item: NotifyItem): boolean {
    return !this.reducedMotion && item.duracion > 0 && item.tipo !== 'cargando';
  }

  onEnter(item: NotifyItem): void {
    if (item.duracion > 0) this.svc.setPaused(item.id, true);
  }

  onLeave(item: NotifyItem): void {
    if (item.duracion > 0) this.svc.setPaused(item.id, false);
  }

  onClose(item: NotifyItem): void {
    this.svc.dismiss(item.id);
  }

  onAction(item: NotifyItem): void {
    item.accion?.run();
    this.svc.dismiss(item.id);
  }

  iconoDe(tipo: NotifyKind): string {
    return tipo;
  }

  /**
   * Descuenta tiempo y desmonta lo que terminó de salir. Corre fuera de la zona,
   * así que hay que volver a entrar para que los signals propaguen el cambio.
   */
  private onTick(): void {
    const list = this.svc.items();

    const pendientes = list.filter((i) => i.saliendo && !this.salidas.has(i.id));
    const hayQueDescontar = list.some(
      (i) => !i.saliendo && !i.paused && i.duracion > 0 && i.tipo !== 'cargando',
    );

    if (!pendientes.length && !hayQueDescontar) return;

    this.zone.run(() => {
      // Programar el desmontaje de las que empezaron a salir.
      for (const item of pendientes) {
        this.salidas.add(item.id);
        setTimeout(() => {
          this.salidas.delete(item.id);
          this.svc.remove(item.id);
        }, this.reducedMotion ? 0 : SALIDA_MS);
      }
      if (hayQueDescontar) this.svc.tick(TICK_MS);
    });
  }
}
